import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, TrendingUp, Download, ArrowRightCircle, FileCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  getEstimatedInflows,
  createEstimatedInflow,
  updateEstimatedInflow,
  deleteEstimatedInflow,
  updateEstimatedInflowStatus,
  convertInflowToInvoice,
  getEstimatedInflowsSummary,
  formatCurrency,
  formatDate,
  handleNumericInput,
  parseNumericValue,
} from "../lib/api";
import { COMPANIES, COMPANY_OPTIONS, getCompanyLabel, COMPANY_COLORS } from "../lib/companies";
import { toast } from "sonner";
import ExportDialog from "../components/ExportDialog";
import GSTInput from "../components/GSTInput";
import { DatePicker } from "../components/DatePicker";

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft", color: "bg-slate-100 text-slate-700 border-slate-200" },
  { value: "pending", label: "Pending", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "pi", label: "PI Raised", color: "bg-violet-50 text-violet-700 border-violet-200" },
  { value: "confirmed", label: "Confirmed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

const StatusBadge = ({ status }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${opt.color}`}
      data-testid={`status-badge-${status}`}
    >
      {opt.label}
    </span>
  );
};

const emptyInflow = {
  company: "empact-studio",
  client_name: "",
  description: "",
  base_amount: "",
  cgst_rate: "",
  sgst_rate: "",
  igst_rate: "",
  tds_rate: "",
  expected_date: new Date().toISOString().split("T")[0],
  status: "draft",
  pi_number: "",
  pi_date: "",
};

export default function EstimatedInflows() {
  const { canEdit } = useAuth();
  const [inflows, setInflows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedInflow, setSelectedInflow] = useState(null);
  const [formData, setFormData] = useState(emptyInflow);
  const [convertForm, setConvertForm] = useState({ invoice_number: "", invoice_date: "", base_amount: "" });
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCompany, setFilterCompany] = useState("all");
  const [savingId, setSavingId] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [inflowsRes, summaryRes] = await Promise.all([
        getEstimatedInflows(filterCompany, filterStatus),
        getEstimatedInflowsSummary(filterCompany),
      ]);
      setInflows(inflowsRes.data);
      setSummary(summaryRes.data);
    } catch {
      toast.error("Failed to load estimated inflows");
    } finally {
      setLoading(false);
    }
  }, [filterCompany, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (inflow = null) => {
    if (inflow) {
      setSelectedInflow(inflow);
      setFormData({
        company: inflow.company || "empact-studio",
        client_name: inflow.client_name,
        description: inflow.description || "",
        base_amount: inflow.base_amount,
        cgst_rate: inflow.cgst_rate,
        sgst_rate: inflow.sgst_rate,
        igst_rate: inflow.igst_rate,
        tds_rate: inflow.tds_rate,
        expected_date: inflow.expected_date,
        status: inflow.status,
        pi_number: inflow.pi_number || "",
        pi_date: inflow.pi_date || "",
      });
    } else {
      setSelectedInflow(null);
      setFormData({
        ...emptyInflow,
        company: filterCompany !== "all" ? filterCompany : "empact-studio",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInflow(null);
    setFormData(emptyInflow);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        base_amount: parseNumericValue(formData.base_amount),
        cgst_rate: parseNumericValue(formData.cgst_rate),
        sgst_rate: parseNumericValue(formData.sgst_rate),
        igst_rate: parseNumericValue(formData.igst_rate),
        tds_rate: parseNumericValue(formData.tds_rate),
        pi_number: formData.status === "pi" ? formData.pi_number : null,
        pi_date: formData.status === "pi" ? formData.pi_date : null,
      };
      if (selectedInflow) {
        await updateEstimatedInflow(selectedInflow.id, submitData);
        toast.success("Estimated inflow updated");
      } else {
        await createEstimatedInflow(submitData);
        toast.success("Estimated inflow created");
      }
      handleCloseDialog();
      fetchData();
    } catch {
      toast.error("Failed to save estimated inflow");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEstimatedInflow(selectedInflow.id);
      toast.success("Estimated inflow deleted");
      setDeleteDialogOpen(false);
      setSelectedInflow(null);
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleStatusChange = async (inflow, newStatus) => {
    try {
      setSavingId(inflow.id);
      await updateEstimatedInflowStatus(inflow.id, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch {
      toast.error("Failed to update status");
    } finally {
      setSavingId(null);
    }
  };

  const openConvertDialog = (inflow) => {
    setSelectedInflow(inflow);
    setConvertForm({
      invoice_number: "",
      invoice_date: new Date().toISOString().split("T")[0],
      base_amount: inflow.base_amount,
    });
    setConvertDialogOpen(true);
  };

  const handleConvertToInvoice = async () => {
    try {
      setSavingId(selectedInflow.id);
      const data = {};
      if (convertForm.invoice_number) data.invoice_number = convertForm.invoice_number;
      if (convertForm.invoice_date) data.invoice_date = convertForm.invoice_date;
      if (convertForm.base_amount !== selectedInflow.base_amount) {
        data.base_amount = parseNumericValue(convertForm.base_amount);
      }
      await convertInflowToInvoice(selectedInflow.id, data);
      toast.success("Converted to invoice successfully!");
      setConvertDialogOpen(false);
      setSelectedInflow(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert");
    } finally {
      setSavingId(null);
    }
  };

  // Summary data
  const totalNet = summary?.total?.net_receivable || 0;
  const totalGST = summary?.future_estimated_gst || 0;
  const totalTDS = summary?.future_estimated_tds || 0;
  const confirmedTotal = summary?.by_status?.confirmed?.total || 0;
  const piTotal = summary?.by_status?.pi?.total || 0;
  const netCashInHand = summary?.total?.net_cash_in_hand || 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="estimated-inflows-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl font-bold text-brand-navy tracking-tight">Estimated Inflows / Billings</h1>
          <p className="text-sm text-slate-500 mt-0.5">Track expected work, PI raised, and convert to invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setExportDialogOpen(true)} data-testid="export-estimated-inflows">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
          {canEdit && (
            <Button onClick={() => handleOpenDialog()} className="bg-brand-orange hover:bg-[#e09515] text-brand-navy font-semibold" data-testid="add-inflow-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Estimate
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterCompany} onValueChange={setFilterCompany}>
          <SelectTrigger className="w-44 h-9 text-sm" data-testid="filter-inflow-company"><SelectValue /></SelectTrigger>
          <SelectContent>
            {COMPANIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="filter-inflow-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="pi">PI Raised</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Strip — 5 cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5 lg:gap-4">
        <Card className="border-l-4 border-l-brand-navy">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Estimated</p>
            <p className="text-sm sm:text-xl md:text-2xl font-bold text-brand-navy tabular-nums mt-1" data-testid="total-estimated-net">{formatCurrency(totalNet)}</p>
            <p className="text-xs text-slate-400 mt-1">{summary?.total?.count || 0} items</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Confirmed + PI</p>
            <p className="text-sm sm:text-xl md:text-2xl font-bold text-emerald-600 tabular-nums mt-1" data-testid="confirmed-total">{formatCurrency(confirmedTotal + piTotal)}</p>
            <p className="text-xs text-slate-400 mt-1">Added to Projected Cash</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Net Cash in Hand</p>
            <p className="text-sm sm:text-xl md:text-2xl font-bold text-amber-600 tabular-nums mt-1" data-testid="net-cash-in-hand">{formatCurrency(netCashInHand)}</p>
            <p className="text-xs text-slate-400 mt-1">Base - TDS (actual bank credit)</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Future Est. GST</p>
            <p className="text-sm sm:text-xl md:text-2xl font-bold text-blue-600 tabular-nums mt-1" data-testid="future-gst">{formatCurrency(totalGST)}</p>
            <p className="text-xs text-slate-400 mt-1">To be collected on billing</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Future Est. TDS</p>
            <p className="text-sm sm:text-xl md:text-2xl font-bold text-red-600 tabular-nums mt-1" data-testid="future-tds">{formatCurrency(totalTDS)}</p>
            <p className="text-xs text-slate-400 mt-1">To be deducted by clients</p>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {STATUS_OPTIONS.map((s) => {
              const data = summary?.by_status?.[s.value];
              return (
                <button
                  key={s.value}
                  onClick={() => setFilterStatus(filterStatus === s.value ? "all" : s.value)}
                  className={`p-3 rounded-lg border text-left transition-all hover:shadow-sm ${
                    filterStatus === s.value ? s.color + " ring-2 ring-offset-1 ring-current" : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`status-filter-${s.value}`}
                >
                  <p className="text-xs font-semibold uppercase tracking-wider opacity-70">{s.label}</p>
                  <p className="text-base sm:text-xl font-bold tabular-nums mt-0.5">{data?.count || 0}</p>
                  <p className="text-xs sm:text-sm tabular-nums text-slate-600">{formatCurrency(data?.total || 0)}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {inflows.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <Table className="min-w-[900px]">
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold">Company</TableHead>
                  <TableHead className="font-semibold">Client / Description</TableHead>
                  <TableHead className="text-right font-semibold">Base Amt</TableHead>
                  <TableHead className="text-right font-semibold">GST</TableHead>
                  <TableHead className="text-right font-semibold">TDS</TableHead>
                  <TableHead className="text-right font-semibold">Net Recv</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">PI Details</TableHead>
                  <TableHead className="text-center font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inflows.map((inflow) => {
                  const gstAmount = (inflow.cgst_amount || 0) + (inflow.sgst_amount || 0) + (inflow.igst_amount || 0);
                  const tdsAmount = inflow.tds_amount || 0;
                  const canConvert = inflow.status === "confirmed" || inflow.status === "pi";
                  return (
                    <TableRow
                      key={inflow.id}
                      data-testid={`inflow-row-${inflow.id}`}
                      className={
                        inflow.status === "confirmed" ? "bg-emerald-50/40" :
                        inflow.status === "pi" ? "bg-violet-50/40" :
                        inflow.status === "pending" ? "bg-amber-50/20" : ""
                      }
                    >
                      <TableCell>
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${COMPANY_COLORS[inflow.company] || COMPANY_COLORS.all}`}>
                          {getCompanyLabel(inflow.company)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">{inflow.client_name}</p>
                        {inflow.description && <p className="text-xs text-slate-500 truncate max-w-[180px]">{inflow.description}</p>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(inflow.base_amount || 0)}</TableCell>
                      <TableCell className="text-right tabular-nums text-blue-600">
                        {gstAmount > 0 ? `+${formatCurrency(gstAmount)}` : <span className="text-slate-300">--</span>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-red-600">
                        {tdsAmount > 0 ? `-${formatCurrency(tdsAmount)}` : <span className="text-slate-300">--</span>}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums text-brand-navy">
                        {formatCurrency(inflow.net_receivable || 0)}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(inflow.expected_date)}</TableCell>
                      <TableCell>
                        <Select
                          value={inflow.status}
                          onValueChange={(val) => handleStatusChange(inflow, val)}
                          disabled={savingId === inflow.id}
                        >
                          <SelectTrigger className="w-[110px] h-8 text-xs border-0 bg-transparent p-0 shadow-none focus:ring-0" data-testid={`status-select-${inflow.id}`}>
                            <StatusBadge status={inflow.status} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="pi">PI Raised</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {inflow.pi_number ? (
                          <div className="text-xs">
                            <p className="font-medium text-violet-700">{inflow.pi_number}</p>
                            {inflow.pi_date && <p className="text-slate-500">{formatDate(inflow.pi_date)}</p>}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300">--</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          {canConvert && (
                            <Button
                              size="sm" variant="outline"
                              className="h-7 text-xs px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => openConvertDialog(inflow)}
                              disabled={savingId === inflow.id}
                              data-testid={`convert-btn-${inflow.id}`}
                            >
                              <ArrowRightCircle className="w-3 h-3 mr-1" /> Invoice
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleOpenDialog(inflow)} data-testid={`edit-inflow-${inflow.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => { setSelectedInflow(inflow); setDeleteDialogOpen(true); }} data-testid={`delete-inflow-${inflow.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-16">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <TrendingUp className="w-7 h-7 text-slate-400" />
              </div>
              <h3 className="text-base font-semibold text-slate-700">No estimated inflows yet</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-xs">Create your first estimate to start tracking expected revenue and billings</p>
              <Button onClick={() => handleOpenDialog()} className="mt-5 bg-brand-orange hover:bg-[#e09515] text-brand-navy font-semibold">
                <Plus className="w-4 h-4 mr-1.5" /> Create Estimate
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Company Breakdown */}
      {inflows.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Company Breakdown</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {COMPANY_OPTIONS.map((company) => {
                const ci = inflows.filter((i) => i.company === company.value);
                const net = ci.reduce((sum, i) => sum + (i.net_receivable || 0), 0);
                const cnt = ci.length;
                return (
                  <div key={company.value} className={`p-4 rounded-lg border ${COMPANY_COLORS[company.value]}`}>
                    <p className="text-xs font-semibold uppercase tracking-wider opacity-70">{company.label}</p>
                    <p className="text-sm sm:text-xl font-bold tabular-nums mt-1">{formatCurrency(net)}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{cnt} estimate{cnt !== 1 ? "s" : ""}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== DIALOGS ===== */}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inflow-dialog">
          <DialogHeader>
            <DialogTitle>{selectedInflow ? "Edit Estimated Inflow" : "New Estimated Inflow"}</DialogTitle>
            <DialogDescription>
              {selectedInflow ? "Update the details below." : "Fill in details to create a new estimate."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company</Label>
                <Select value={formData.company} onValueChange={(v) => setFormData({ ...formData, company: v })}>
                  <SelectTrigger data-testid="select-inflow-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMPANY_OPTIONS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="select-inflow-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="pi">PI Raised</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* PI Fields — only when status = pi */}
            {formData.status === "pi" && (
              <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-violet-50 border border-violet-200">
                <div>
                  <Label className="text-violet-700">PI Number</Label>
                  <Input
                    value={formData.pi_number}
                    onChange={(e) => setFormData({ ...formData, pi_number: e.target.value })}
                    placeholder="PI-2026-001"
                    required
                    data-testid="input-inflow-pi-number"
                  />
                </div>
                <div>
                  <Label className="text-violet-700">PI Date</Label>
                  <DatePicker
                    value={formData.pi_date}
                    onChange={(val) => setFormData({ ...formData, pi_date: val })}
                    data-testid="input-inflow-pi-date"
                  />
                </div>
              </div>
            )}

            <div>
              <Label>Client Name</Label>
              <Input value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} placeholder="Client / Company Name" required data-testid="input-inflow-client" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Service / Project description" data-testid="input-inflow-description" />
            </div>
            <div>
              <Label>Base Amount (Rs.)</Label>
              <Input type="text" inputMode="decimal" value={formData.base_amount} onChange={(e) => setFormData({ ...formData, base_amount: handleNumericInput(e.target.value) })} placeholder="0" required data-testid="input-inflow-amount" />
            </div>
            <GSTInput
              cgst_rate={formData.cgst_rate}
              sgst_rate={formData.sgst_rate}
              igst_rate={formData.igst_rate}
              handleNumericInput={handleNumericInput}
              onChange={(vals) => setFormData((prev) => ({ ...prev, ...vals }))}
            />
            <div>
              <Label className="text-red-600 text-xs">TDS Rate (%) — Deducted by Client</Label>
              <Select value={formData.tds_rate?.toString() || "0"} onValueChange={(v) => setFormData({ ...formData, tds_rate: v })}>
                <SelectTrigger data-testid="input-inflow-tds"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No TDS</SelectItem>
                  <SelectItem value="1">1% — Insurance Commission</SelectItem>
                  <SelectItem value="2">2% — Contractors</SelectItem>
                  <SelectItem value="5">5% — Commission</SelectItem>
                  <SelectItem value="10">10% — Professional Fees</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expected Date</Label>
              <DatePicker value={formData.expected_date} onChange={(val) => setFormData({ ...formData, expected_date: val })} data-testid="input-inflow-date" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" className="bg-brand-orange hover:bg-[#e09515] text-brand-navy font-semibold" data-testid="save-inflow">
                {selectedInflow ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Estimated Inflow</AlertDialogTitle>
            <AlertDialogDescription>
              Delete estimate for "{selectedInflow?.client_name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-inflow">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert to Invoice Dialog — with editable fields */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-md" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-emerald-600" />
              Convert to Invoice
            </DialogTitle>
            <DialogDescription>
              Create an invoice from this estimate. You can adjust the details below.
            </DialogDescription>
          </DialogHeader>
          {selectedInflow && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-xs text-slate-500">Estimate</p>
                <p className="font-semibold text-sm">{selectedInflow.client_name}</p>
                <p className="text-xs text-slate-500">{selectedInflow.description}</p>
                <p className="text-sm font-medium mt-1">{formatCurrency(selectedInflow.base_amount)} base &middot; {formatCurrency(selectedInflow.net_receivable)} net</p>
              </div>
              <div>
                <Label>Invoice Number</Label>
                <Input
                  value={convertForm.invoice_number}
                  onChange={(e) => setConvertForm({ ...convertForm, invoice_number: e.target.value })}
                  placeholder="Leave blank for auto-generated"
                  data-testid="convert-invoice-number"
                />
                <p className="text-xs text-slate-400 mt-0.5">Leave empty for auto-generated number</p>
              </div>
              <div>
                <Label>Invoice Date</Label>
                <DatePicker
                  value={convertForm.invoice_date}
                  onChange={(val) => setConvertForm({ ...convertForm, invoice_date: val })}
                  data-testid="convert-invoice-date"
                />
              </div>
              <div>
                <Label>Base Amount (Rs.)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={convertForm.base_amount}
                  onChange={(e) => setConvertForm({ ...convertForm, base_amount: handleNumericInput(e.target.value) })}
                  data-testid="convert-invoice-amount"
                />
                <p className="text-xs text-slate-400 mt-0.5">GST & TDS will be recalculated if amount changes</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleConvertToInvoice}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                  disabled={savingId === selectedInflow?.id}
                  data-testid="confirm-convert-inflow"
                >
                  <ArrowRightCircle className="w-4 h-4 mr-1.5" /> Create Invoice
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <ExportDialog open={exportDialogOpen} onOpenChange={setExportDialogOpen} dataType="estimated-inflows" title="Estimated Inflows" showDateFilter={true} showCompanyFilter={true} />
    </div>
  );
}
